import { useEffect } from 'react';
import { apiClient } from '@/api/client';
import { getCurrentPushToken } from '@/lib/push-notifications';

/**
 * Keeps the backend's FCM token for this browser in sync with the LIVE push
 * subscription, on every app load.
 *
 * This is the fix for the silent-death class of push bugs: Chrome rotates the
 * push subscription (SW updates, cleared site data, key rotation), the token in
 * the DB goes stale, and FCM then ACCEPTS sends to it (200) while silently
 * dropping them — nothing errors anywhere. Firebase's guidance is to re-fetch
 * the token on every startup and update the server (the register endpoint
 * upserts by token, so this is idempotent and also bumps lastSeenAt).
 *
 * Never prompts: getCurrentPushToken() is a no-op unless permission is already
 * granted and push is configured for this environment. Best-effort by design —
 * a failure here must never affect the app; the next load retries.
 *
 * Mounted once, app-wide, from the always-present NotificationBell.
 */
export function usePushTokenSync(): void {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getCurrentPushToken();
        if (!token || cancelled) return;
        await apiClient.post('/api/notifications/push-subscriptions', {
          fcmToken: token,
          userAgent: navigator.userAgent,
        });
      } catch {
        // Best-effort resync; retried on the next app load.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
